import { useStyletron } from '@thanx/uikit/theme'
import React from 'react'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

export interface MultiSelectHeaderProps
  extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode
}

interface SectionProps {
  children?: React.ReactNode
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Controlled MultiSelectHeader layout component.
 *
 * Provides the header layout structure with left and right sections.
 * Use with sub-components: SelectAllToggle, SelectedCount, PaginationInfo, PaginationControls.
 *
 * @example
 * ```tsx
 * <MultiSelectHeader>
 *   <MultiSelectHeader.Left>
 *     <SelectAllToggle checked={allSelected} onToggle={handleToggle} />
 *     <SelectedCount count={5} onClick={handleShowSelections} />
 *   </MultiSelectHeader.Left>
 *   <MultiSelectHeader.Right>
 *     <PaginationInfo startItem={1} endItem={50} totalCount={100} />
 *     <PaginationControls hasPrevPage={false} hasNextPage={true} onNextPage={handleNext} />
 *   </MultiSelectHeader.Right>
 * </MultiSelectHeader>
 * ```
 */
const MultiSelectHeader = React.memo(
  ({ children, style, className, ...props }: MultiSelectHeaderProps) => {
    const [css, theme] = useStyletron()

    const headerClassName = css(getHeaderStyles(theme))

    return (
      <div
        className={`${headerClassName} ${className || ''}`}
        style={style}
        {...props}
      >
        {children}
      </div>
    )
  }
)

// ═══════════════════════════════════════════════════════════════════════════
// Sub-components for layout
// ═══════════════════════════════════════════════════════════════════════════

const Left = React.memo(({ children }: SectionProps) => {
  const [css] = useStyletron()

  const leftSectionClassName = css(getLeftSectionStyles())

  return <div className={leftSectionClassName}>{children}</div>
})

Left.displayName = 'MultiSelectHeader.Left'

const Right = React.memo(({ children }: SectionProps) => {
  const [css] = useStyletron()

  const rightSectionClassName = css(getRightSectionStyles())

  return <div className={rightSectionClassName}>{children}</div>
})

Right.displayName = 'MultiSelectHeader.Right'

// Attach sub-components
const MultiSelectHeaderWithSections = Object.assign(MultiSelectHeader, {
  Left,
  Right,
})

MultiSelectHeader.displayName = 'MultiSelectHeader'

// ═══════════════════════════════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════════════════════════════

const getHeaderStyles = (theme: any) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid' as const,
  borderBottomColor: theme.colors.grey30,
  backgroundColor: theme.colors.white,
})

const getLeftSectionStyles = () => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
})

const getRightSectionStyles = () => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
})

export default MultiSelectHeaderWithSections
